"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type FormEvent } from "react";

import styles from "./ManagedServiceIntake.module.css";
import { submitMigration } from "@/migration-actions";

type Membership = "member" | "guest";
type IntakeValues = { name: string; email: string; organization: string; platform: string; users: string; timing: string };

const EMPTY_INTAKE: IntakeValues = { name: "", email: "", organization: "", platform: "", users: "", timing: "" };

export function ManagedServiceIntake() {
  const [membership, setMembership] = useState<Membership>("guest");

  useEffect(() => {
    const syncMembership = () => {
      if (window.location.hash === "#migration-member") setMembership("member");
      else if (window.location.hash === "#migration-non-member") setMembership("guest");
    };
    syncMembership();
    window.addEventListener("hashchange", syncMembership);
    return () => window.removeEventListener("hashchange", syncMembership);
  }, []);

  const chooseMembership = (next: Membership) => {
    setMembership(next);
    window.history.replaceState(window.history.state, "", next === "member" ? "#migration-member" : "#migration-non-member");
  };

  return (
    <section id="managed-services" className={styles.surface}>
      <div className={styles.layout}>
        <div className={styles.intro}>
          <p className={styles.eyebrow}>PSCX MIGRATION SERVICES</p>
          <h1>{membership === "guest" ? "Expert-led migration support for non-members." : "Migration support for Edge members."}</h1>
          <p className={styles.lede}>{membership === "guest" ? "Engage PSCX’s dedicated services team to plan and execute your migration with clear scope, transparent pricing, and enterprise-grade delivery." : "Connect with the Edge team to plan your included migration. An engineer will review your environment and help coordinate your move."}</p>
          <ul className={styles.assurances}>
            {["Structured discovery and migration planning", "Scope and eligibility confirmed before work begins", membership === "guest" ? "Implementation pricing aligned to project complexity" : "One hosted-platform migration included every 24 months"].map((text, index) => <li key={text}>
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                {index === 0 ? <><rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V2h6v2M9 9h6M9 13h6M9 17h4"/></> : index === 1 ? <><path d="M12 3 4 6v6c0 5 8 9 8 9s8-4 8-9V6z"/><path d="m8 12 3 3 5-6"/></> : <><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18M7 15h4"/></>}
              </svg><span>{text}</span>
            </li>)}
          </ul>
          {membership === "guest" ? (
            <a href="#bundles" className="mt-8 inline-flex min-h-11 items-center gap-3 rounded-lg border border-ink bg-ink px-5 text-sm font-semibold text-paper-raised transition-colors hover:bg-ink-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink">Learn more about becoming an Edge member <span aria-hidden="true">→</span></a>
          ) : (
            <a href="#bundles" className="mt-8 inline-flex min-h-11 items-center gap-3 text-sm font-semibold text-ink underline underline-offset-4 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink">Learn more about our bundles <span aria-hidden="true">→</span></a>
          )}
          <p className={styles.legal}>Terms and conditions apply.</p>
        </div>
        <div className={styles.workflow}>
          <p className="mb-3 text-sm font-semibold text-ink">Select your engagement path</p>
          <div role="group" aria-label="Select your engagement path" className={styles.engagementToggle} data-selected={membership}>
            <span aria-hidden="true" className={styles.selectionSlide} />
            <RouteChoice
              id="migration-member"
              active={membership === "member"}
              title="Edge member"
              onClick={() => chooseMembership("member")}
            />
            <RouteChoice
              id="migration-non-member"
              active={membership === "guest"}
              title="Non-member"
              onClick={() => chooseMembership("guest")}
            />
          </div>

          <div className="mt-5 min-h-[19rem]">
            {membership === "member" ? <MemberRoute /> : <MigrationIntake />}
          </div>
        </div>
      </div>
    </section>
  );
}

function RouteChoice({ id, active, title, onClick }: { id: string; active: boolean; title: string; onClick: () => void }) {
  return (
    <button
      id={id}
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`${styles.engagementOption} ${active ? styles.activeOption : ""}`}
    >
      {title}
    </button>
  );
}

function MemberRoute() {
  return (
    <div className="grid min-h-[19rem] content-between py-5 text-ink">
      <div>
        <p className="font-sans text-xs font-semibold uppercase tracking-[0.18em] text-signal-ink">Included with your Edge membership</p>
        <h3 className="mt-3 text-display-sm font-semibold text-ink">One hosted-platform migration every 24 months.</h3>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate">
          Reach out to edge@pscx.ai. An engineer will schedule time with you to review the next steps for your migration.
        </p>
      </div>
      <a href="mailto:edge@pscx.ai" className="mt-8 inline-flex w-fit items-center gap-2 rounded-[4px] bg-cta px-5 py-3 font-sans text-sm font-semibold text-cta-ink transition-colors hover:bg-cta-hover">
        Email edge@pscx.ai <span aria-hidden="true">↗</span>
      </a>
    </div>
  );
}

function MigrationIntake() {
  const [values, setValues] = useState<IntakeValues>(EMPTY_INTAKE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const sending = useRef(false);

  const update = (key: keyof IntakeValues, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => { const next = { ...current }; delete next[key]; return next; });
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending.current) return;
    const website = String(new FormData(event.currentTarget).get("website") ?? "");
    const nextErrors: Record<string, string> = {};
    for (const [key, value] of Object.entries(values)) {
      if (!value.trim()) nextErrors[key] = "Please complete this field.";
    }
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) nextErrors.email = "Enter a valid email address.";
    if (values.users && (!Number.isSafeInteger(Number(values.users)) || Number(values.users) < 1 || Number(values.users) > 1000000)) nextErrors.users = "Enter a whole number between 1 and 1,000,000.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    sending.current = true;
    setPending(true);
    try {
      const result = await submitMigration({ ...values, website });
      if (result.ok) setSent(true);
      else setErrors(result.errors);
    } catch {
      setErrors({ _form: "We couldn’t send your request. Please try again or email edge@pscx.ai." });
    } finally {
      sending.current = false;
      setPending(false);
    }
  };

  return (
    <section aria-label="Migration support request">
      <div className={styles.vipBanner}>
        <Image src="/brand/pscx-vip-combined.png" alt="PSCX VIP" width={176} height={51} className="h-auto w-24 shrink-0" />
        <p>Requests are securely routed through our Professional Service Platform, PSCX VIP.</p>
      </div>
      <div className="pt-6">
        {sent ? <div role="status" className="py-6">
          <h3 className="text-xl font-semibold text-ink">Request sent</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate">Thank you. Our team will contact you at {values.email.trim()} to review the next steps for your migration.</p>
        </div> : <form noValidate onSubmit={submit} aria-busy={pending}>
          <h3 className="font-display text-2xl font-semibold tracking-tight text-ink">Request a migration consultation</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate">Share a few details about your environment. A migration specialist will review your request and contact you with recommended next steps.</p>
          <p className="mt-4 text-xs leading-5 text-slate">Required fields marked with * · Typical response within one business day.</p>
          <fieldset disabled={pending} className="mt-6 grid min-w-0 gap-5 sm:grid-cols-2">
            <IntakeField field="name" label="Name" placeholder="Full name" value={values.name} error={errors.name} autoComplete="name" maxLength={120} onChange={update} />
            <IntakeField field="email" label="Work email" placeholder="you@company.com" type="email" value={values.email} error={errors.email} autoComplete="email" maxLength={200} onChange={update} />
            <IntakeField field="organization" label="Organization" placeholder="Company or organization" value={values.organization} error={errors.organization} autoComplete="organization" maxLength={160} onChange={update} />
            <IntakeField field="platform" label="Current platform" placeholder="Select your current platform" value={values.platform} error={errors.platform} options={["RingCentral", "Microsoft Teams", "Zoom", "Cisco", "Genesys", "Other"]} onChange={update} />
            <IntakeField field="users" label="Estimated user count" placeholder="e.g. 250" type="number" value={values.users} error={errors.users} onChange={update} />
            <IntakeField field="timing" label="Target migration timeframe" placeholder="Select your timeframe" value={values.timing} error={errors.timing} options={["Within 3 months", "3–6 months", "6–12 months", "Exploring options"]} onChange={update} />
          </fieldset>
          <div className="absolute -left-[10000px]" aria-hidden="true"><label htmlFor="migration-website">Website</label><input id="migration-website" name="website" tabIndex={-1} autoComplete="off" /></div>
          {errors._form ? <p role="alert" className="mt-5 text-sm text-signal-ink">{errors._form}</p> : null}
          <div className="mt-7">
            <button disabled={pending} type="submit" className="flex min-h-12 w-full items-center justify-center gap-3 rounded-md bg-cta px-5 py-3 text-sm font-semibold text-cta-ink hover:bg-cta-hover focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink disabled:cursor-wait disabled:opacity-70">{pending ? "Sending…" : "Request consultation"}<span aria-hidden="true">→</span></button>
            <p className="mt-4 text-center text-xs leading-5 text-slate">Prefer email? Contact <a href="mailto:edge@pscx.ai" className="font-medium text-ink underline underline-offset-4">edge@pscx.ai</a></p>
            <p className="mt-4 text-center text-xs leading-5 text-slate">Your information is used only to evaluate and respond to this request.</p>
          </div>
        </form>}
      </div>
    </section>
  );
}

function IntakeField({ field, label, value, error, type = "text", options, autoComplete, maxLength, placeholder, onChange }: {
  field: keyof IntakeValues; label: string; value: string; error?: string;
  type?: "text" | "email" | "number"; options?: string[]; autoComplete?: string; maxLength?: number; placeholder?: string;
  onChange: (key: keyof IntakeValues, value: string) => void;
}) {
  const id = `vip-${field}`;
  const className = "h-12 w-full min-w-0 rounded-md border border-hairline bg-white px-3 text-base text-slate-strong outline-none transition-colors focus:border-ink focus:ring-2 focus:ring-ink/15";
  const common = { id, name: field, required: true, value, "aria-invalid": !!error, "aria-describedby": error ? `${id}-error` : undefined, className };
  return <div className="min-w-0">
    <label htmlFor={id} className="mb-2 block font-sans text-sm font-medium text-ink">{label} <span aria-hidden="true">*</span></label>
    {options ? <select {...common} onChange={(event) => onChange(field, event.target.value)}>
      <option value="">{placeholder ?? "Choose an option"}</option>
      {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select> : <input {...common} type={type} placeholder={placeholder} autoComplete={autoComplete} maxLength={maxLength} min={type === "number" ? 1 : undefined} max={type === "number" ? 1000000 : undefined} step={type === "number" ? 1 : undefined} onChange={(event) => onChange(field, event.target.value)} />}
    {error ? <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-signal-ink">{error}</p> : null}
  </div>;
}
